import { normalizeAuditStatus } from '@/features/AuditLogs/utils/auditLogUtils'
import type { AuditLogs } from '@/generated/http-clients/v3ApiSchemas'
import type { TFunction } from 'i18next'

const LICENSE_ACTION_PREFIXES = ['license:', 'license.']

const ACTION_LABEL_KEYS: Record<string, string> = {
  create: 'created',
  renew: 'renewed',
  revoke: 'revoked',
  reactivate: 'reactivated',
  updateEntitlements: 'entitlementsUpdated',
  updateMetadata: 'metadataUpdated',
}

/**
 * Reads the license action verb from an audit event's request metadata.
 * The action (e.g. `license:revoke`) is the only field that says what actually
 * happened; `resource` is always `{ id, type: 'license' }` and carries none.
 */
export function getLicenseAction(log: AuditLogs): string | undefined {
  const action = log.request_metadata?.action
  return typeof action === 'string' ? action : undefined
}

function humanizeVerb(verb: string): string {
  const words = verb
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[.-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function stripLicensePrefix(action: string): string {
  const prefix = LICENSE_ACTION_PREFIXES.find((p) => action.startsWith(p))
  return prefix ? action.slice(prefix.length) : action
}

export function formatLicenseAction(
  action: string | undefined,
  t: TFunction,
): string {
  if (!action) {
    return String(t('licenses.detail.history.actions.unknown'))
  }
  const verb = stripLicensePrefix(action)
  if (Object.hasOwn(ACTION_LABEL_KEYS, verb)) {
    return String(
      t(`licenses.detail.history.actions.${ACTION_LABEL_KEYS[verb]}`),
    )
  }
  return humanizeVerb(verb)
}

const FAILURE_STATUSES = ['failure', 'error']

export function getLicenseStatusColorPalette(status?: string): string {
  const normalized = normalizeAuditStatus(status)
  if (normalized === 'success') {
    return 'green'
  }
  if (normalized === 'denied') {
    return 'orange'
  }
  if (normalized && FAILURE_STATUSES.includes(normalized)) {
    return 'red'
  }
  return 'gray'
}

export function getLicenseStatusColor(status?: string): string {
  const palette = getLicenseStatusColorPalette(status)
  return palette === 'gray' ? 'gray.400' : `${palette}.500`
}

export function formatLicenseStatus(
  status: string | undefined,
  t: TFunction,
): string {
  const normalized = normalizeAuditStatus(status)
  if (!normalized) {
    return '-'
  }
  const translated = t(`licenses.detail.history.status.${normalized}`, {
    defaultValue: '',
  })
  if (translated) {
    return String(translated)
  }
  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}
